import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, TouchableOpacity, FlatList, Image, Text, ActivityIndicator, ScrollView } from 'react-native-web';
import { useRoute } from '@react-navigation/native';
import { toast } from 'react-hot-toast';
import * as Animatable from 'react-native-animatable';
import FeatherIcon from 'react-native-vector-icons/dist/Feather';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import imageCompression from 'browser-image-compression';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';

// Custom Components
import LoadingView from '../../components/component_loading_view';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';

// Configs
import GLOBAL from '../../configs/config_global';
import { setGlobalState, STATE_KEYS } from '../../configs/config_state';

// APIs
import { addTruckPhotoMulti, getAlbumPhotos, getLastPhotoPosition, deleteMultiTruckPhoto, updatePhotoOrder } from '../../modules';

// Utils
import { showAlert, showAppError } from '../../utils/util_helpers';
import { NavBar } from '../../components';

// region Helper - Move Array Item
const arrayMove = (list, from, to) => {
    if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
    const next = list.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
};
// endregion

// region Memoized Photo Grid Item
const PhotoGridItem = React.memo(({ item, index, isSelected, isDeleting, onDeleteMode, onPress }) => (
    <TouchableOpacity
        disabled={isDeleting}
        onPress={() => onPress(item.id, index)}
        style={{ flex: 1, height: 300, backgroundColor: GLOBAL.COLOR.TEXT_LIGHT_GRAY }}>
        <Image
            source={{ uri: item.src }}
            style={{ width: '100%', height: 300 }}
            resizeMode={'cover'}
        />

        {/* Removal Chosen Overlay */}
        {
            isSelected &&
            <View style={{
                position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
                alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
            }}>
                <FeatherIcon name={'check'} size={40} color={'white'} />
            </View>
        }
    </TouchableOpacity>
));
// endregion

// region Helper - Get Column Count
const getNumColumns = (width) => {
    if (width > 2400) return 8;
    if (width > 1800) return 6;
    if (width > 1200) return 5;
    if (width > 600) return 4;
    if (width > 300) return 2;
    return 1;
};
// endregion

// region Memoized Sortable Photo Item
const SortablePhotoItem = React.memo(({ item, index, position, cellWidthPct, cellHeight }) => {
    const { ref, isDragging } = useSortable({ id: item.id, index });

    return(
        <div
            ref={ref}
            style={{
                width: cellWidthPct,
                height: cellHeight,
                position: 'relative',
                backgroundColor: GLOBAL.COLOR.TEXT_LIGHT_GRAY,
                opacity: isDragging ? 0.4 : 1,
                cursor: isDragging ? 'grabbing' : 'grab',
                touchAction: 'none',
                userSelect: 'none',
                boxSizing: 'border-box',
                overflow: 'hidden'
            }}>
            <img
                src={item.src}
                alt={item.title}
                draggable={false}
                style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none', display: 'block' }}
            />

            {/* Position Badge */}
            <View style={{
                position: 'absolute', left: 8, top: 8,
                minWidth: 28, height: 28, paddingHorizontal: 8, borderRadius: 14,
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                alignItems: 'center', justifyContent: 'center'
            }}>
                <Text style={{ color: 'white', fontSize: 14, fontWeight: '700' }}>
                    {position}
                </Text>
            </View>
        </div>
    );
});
// endregion

export function ProfilePhotoEdit() {
    const MAX_PHOTO_COUNT = 5;

    // region Route Params
    const {
        truckData = {},
        photoAlbumID = -1,
        onPhotoAlbumUpdated = () => {}
    } = useRoute()?.params || {};
    // endregion

    // region Refs
    const cachedPhotoData = useRef([]); // This is for the re-ordering feature
    const photoPicker = useRef(null);
    const imageGrid = useRef(null);
    const onDeleteModeRef = useRef(false);
    // endregion

    // region Local State
    const [isLoading, setLoading] = useState(true);
    const [onDeleteMode, setDeleteMode] = useState(false);
    const [deletingIDs, setDeletingIDs] = useState(new Set());
    const [isDeleting, setDeleting] = useState(false);
    const [onArrangeMode, setArrangeMode] = useState(false);
    const [isSavingOrder, setSavingOrder] = useState(false);
    const [photoData, setPhotoData] = useState([]);
    const [photoCarouselOpened, setPhotoCarouselOpened] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [containerLayout, setContainerLayout] = useState({ width: 0, height: 0 });
    // endregion

    // region Derived State
    const numColumns = useMemo(() => getNumColumns(containerLayout.width), [containerLayout.width]);
    // endregion

    // region Sync Refs
    onDeleteModeRef.current = onDeleteMode;
    // endregion

    // region Stable Callbacks
    const handlePhotoPress = useCallback((itemId, index) => {
        if (onDeleteModeRef.current) {
            setDeletingIDs((prev) => {
                const next = new Set(prev);
                if (next.has(itemId)) next.delete(itemId);
                else next.add(itemId);
                return next;
            });
        } else {
            setCurrentPhotoIndex(index);
            setPhotoCarouselOpened(true);
        }
    }, []);

    const keyExtractor = useCallback((item) => item.key, []);
    // endregion

    // region Component Mounting
    useEffect(() => {
        setTimeout(() => goFetchTruckPhotos(), 500);
    }, []);
    // endregion

    // region Function - Generate Photo Data List
    const generatePhotoDataList = (rawList, totalPhotoCount = 0) => {
        const newList = rawList
            .map((item, index) => ({
                key: `imageItem_${item.id}`,
                src: item['src'] || item['path_webp'] || item['path'],
                id: item.id,
                position: Number(item.position),
                title: `Photo ${index + 1} of ${totalPhotoCount}`
            }))
            .sort((a, b) => Number(a['position']) - Number(b['position']));

        return(newList);
    }
    // endregion

    // region Function - Get Truck Photos (Progressive Loading)
    const goFetchTruckPhotos = () => {
        setLoading(true);
        getAlbumPhotos(truckData.id, photoAlbumID, 1, 20)
            .then((firstResult) => {
                const totalPhotoCount = firstResult['totalResult'];
                const firstPageData = generatePhotoDataList(firstResult.data, totalPhotoCount);

                // Show first page immediately
                cachedPhotoData.current = firstPageData;
                setPhotoData(firstPageData);
                setLoading(false);

                // Load remaining pages in background
                const pageArr = [];
                for (let i = 2; i <= firstResult.lastPage; ++i) {
                    pageArr.push(i);
                }

                if (pageArr.length === 0) return;

                Promise.all(pageArr.map((page) => getAlbumPhotos(truckData.id, photoAlbumID, page, 20)))
                    .then((resultArr) => {
                        let totalList = [].concat(firstResult.data);
                        resultArr.forEach((resultItem) => {
                            if (resultItem) {
                                totalList = totalList.concat(resultItem.data);
                            }
                        });

                        const allPhotoData = generatePhotoDataList(totalList, totalPhotoCount);
                        cachedPhotoData.current = allPhotoData;
                        setPhotoData(allPhotoData);
                    })
                    .catch((error) => showAppError(error));
            })
            .catch((error) => {
                showAppError(error);
                setLoading(false);
            });
    };
    // endregion

    // region Function - Go Upload Photo
    const imageCompressionOptions = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
    const goUploadImageList = (pendingUploadPhotos) => {
        if (pendingUploadPhotos.length <= 0) return;

        setGlobalState(STATE_KEYS.LOADING_CONFIG, { loadingText: 'Uploading Photos: 0%' });

        // Compress images
        Promise
            .all(pendingUploadPhotos.map((photo) => imageCompression(photo, imageCompressionOptions)))
            .then((compressedImages) => {
                // Get last photo position
                getLastPhotoPosition(truckData.id, photoAlbumID)
                    .then((position) => {
                        // Start upload process
                        addTruckPhotoMulti(truckData.id, photoAlbumID, position + 1, compressedImages, (progress) => setGlobalState(STATE_KEYS.LOADING_CONFIG, { loadingText: `Uploading Photos: ${progress?.toFixed(2)}%` }))
                            .then((newImageList) => {
                                // Because the server will upload images to the local storage of it first
                                // so the initial returned links are "local server" links
                                // which will quickly become invalid as soon as they are uploaded to S3
                                // Solution:
                                // 1. After upload is finished, extract the newly uploaded elements from the response.
                                // 2. Replace each of them with the device local link accordingly (in the tempImageList array).
                                let uploadedItems = newImageList.slice(Math.max(newImageList.length - pendingUploadPhotos.length, 0)),
                                    replacedItems = uploadedItems.map((item, index) => Object.assign({}, item, { path: URL.createObjectURL(pendingUploadPhotos[index]) }));

                                const newPhotoData = photoData.concat(generatePhotoDataList(replacedItems, newImageList.length));
                                const uploadedCount = pendingUploadPhotos.length;

                                // Save to cache
                                cachedPhotoData.current = newPhotoData;

                                // Save to state
                                setPhotoData(newPhotoData);

                                // Callback
                                onPhotoAlbumUpdated(newPhotoData, newPhotoData.length);

                                // Show toast
                                toast(uploadedCount + ` image${pendingUploadPhotos.length > 1 ? 's' : ''} added to your truck gallery`);

                                // Scroll list to end
                                setTimeout(() => imageGrid.current?.scrollToEnd(), 1000);
                            })
                            .catch((error) => showAppError(error))
                            .finally(() => {
                                setGlobalState(STATE_KEYS.LOADING_CONFIG, null);
                            });
                    })
                    .catch((positionError) => {
                        showAppError(positionError);
                        setGlobalState(STATE_KEYS.LOADING_CONFIG, null);
                    });
            })
            .catch((error) => {
                showAppError(error.message);
                setGlobalState(STATE_KEYS.LOADING_CONFIG, null);
            });
    }
    // endregion

    // region Function - Go Delete Images
    const goDeleteImages = () => {
        setDeleting(true);
        const deletingIDArray = [...deletingIDs];
        setTimeout(() => {
            deleteMultiTruckPhoto(truckData.id, photoAlbumID, deletingIDArray)
                .then(() => {
                    // Remove the deleted photos
                    const newPhotoData = photoData.filter((item) => !deletingIDs.has(item.id));
                    const generatedData = generatePhotoDataList(newPhotoData, newPhotoData.length);

                    // Update cached data
                    cachedPhotoData.current = generatedData;

                    // Update scene_truck component data and notify user
                    onPhotoAlbumUpdated(generatedData, generatedData.length);
                    toast(deletingIDArray.length + ` photo${deletingIDArray.length > 1 ? 's' : ''} removed from your truck photo album`);

                    // Set new data to state
                    setPhotoData(generatedData);
                    setDeleteMode(false);
                    setDeletingIDs(new Set());
                })
                .catch((error) => showAppError(error))
                .finally(() => setDeleting(false));
        }, 2000);
    };
    // endregion

    // region Function - On Delete Mode Cancel
    const _onDeleteModeCancelPressed = () => {
        setDeleteMode(false);
        setDeletingIDs(new Set());
    }
    // endregion

    // region Function - Go Update Photo Order
    const goUpdatePhotoOrder = () => {
        if (isSavingOrder) return;

        const sortArr = photoData.map((item, index) => ({ photo_id: item.id, position: index + 1 }));

        setSavingOrder(true);
        updatePhotoOrder(truckData.id, photoAlbumID, sortArr)
            .then(() => {
                const normalized = photoData.map((item, index) => ({ ...item, position: index + 1 }));
                cachedPhotoData.current = normalized;
                setPhotoData(normalized);
                onPhotoAlbumUpdated(normalized, normalized.length);
                setArrangeMode(false);
                toast('Photo order updated');
            })
            .catch((error) => showAppError(error))
            .finally(() => setSavingOrder(false));
    };
    // endregion

    // region Function - On Arrange Mode Cancel
    const _onArrangeModeCancelPressed = () => {
        setPhotoData(cachedPhotoData.current);
        setArrangeMode(false);
    };
    // endregion

    // region Function - On Drag End
    const _onDragEnd = useCallback((event) => {
        if (event.canceled) return;
        const source = event.operation?.source;
        if (!source?.sortable) return;
        const from = source.sortable.initialIndex;
        const to = source.sortable.index;
        if (from === to) return;
        setPhotoData((prev) => arrayMove(prev, from, to));
    }, []);
    // endregion

    // region Render FlatList Item
    const renderPhotoItem = useCallback(({ item, index }) => (
        <PhotoGridItem
            item={item}
            index={index}
            isSelected={deletingIDs.has(item.id)}
            isDeleting={isDeleting}
            onDeleteMode={onDeleteMode}
            onPress={handlePhotoPress}
        />
    ), [deletingIDs, onDeleteMode, isDeleting, handlePhotoPress]);
    // endregion

    // region Render Toolbar
    const TOOLBAR_HEIGHT = 64;
    const renderToolbarItem = (icon, text, onPress = () => {}, disabled = false) => (
        <TouchableOpacity
            onPress={onPress}
            disabled={disabled}
            style={{ height: TOOLBAR_HEIGHT, marginHorizontal: 24, flexDirection: 'row', alignItems: 'center', opacity: disabled ? 0.5 : 1 }}>
            <FeatherIcon name={icon} size={24} color={'white'} />
            <Text style={{ color: 'white', fontSize: 16, fontWeight: '500', marginLeft: 8 }}>
                {text}
            </Text>
        </TouchableOpacity>
    );

    const renderArrangeToolbar = () => (
        <View style={{ width: '100%' }}>
            <View style={{ width: '100%', flexDirection: 'row', alignItems: 'center', margin: 24, marginBottom: isSavingOrder ? 24 : 0 }}>
                {isSavingOrder && <ActivityIndicator size={32} color={'white'} />}

                <Text style={{ color: 'white', fontSize: 16, fontWeight: '500', marginLeft: 16 }}>
                    {isSavingOrder ? 'Saving photo order...' : 'Drag photos to re-arrange their order'}
                </Text>
            </View>

            {
                !isSavingOrder &&
                <>
                    <View style={{ width: '100%', height: 1, backgroundColor: 'white', marginTop: 24 }} />
                    <View style={{ alignItems: 'center', justifyContent: 'center', flexDirection: 'row', width: '100%' }}>
                        {renderToolbarItem('check', 'Save', goUpdatePhotoOrder)}
                        {renderToolbarItem('x-circle', 'Cancel', _onArrangeModeCancelPressed)}
                    </View>
                </>
            }
        </View>
    );

    const renderToolbar = () => {
        let content;
        if (onArrangeMode) {
            content = renderArrangeToolbar();
        }
        else if (onDeleteMode) {
            content = (
                <View style={{ width: '100%' }}>
                    <View style={{ width: '100%', flexDirection: 'row', alignItems: 'center', margin: 24, marginBottom: isDeleting ? 24 : 0 }}>
                        {isDeleting && <ActivityIndicator size={32} color={'white'} />}

                        <Text style={{ color: 'white', fontSize: 16, fontWeight: '500', marginLeft: 16 }}>
                            {
                                isDeleting ?
                                    'Removing select photos...'
                                    :
                                    (deletingIDs.size ? `${deletingIDs.size} photos selected for removal` : 'Click to select the photos that you want to remove')
                            }
                        </Text>
                    </View>

                    {
                        !isDeleting &&
                        <>
                            <View style={{ width: '100%', height: 1, backgroundColor: 'white', marginTop: 24 }} />
                            <View style={{ alignItems: 'center', justifyContent: 'center', flexDirection: 'row', width: '100%' }}>
                                {renderToolbarItem('trash-2', 'Confirm', () => goDeleteImages(), !deletingIDs.size)}
                                {renderToolbarItem('x-circle', 'Cancel', _onDeleteModeCancelPressed)}
                            </View>
                        </>
                    }
                </View>
            );
        }
        else {
            content = (
                <View style={{ alignItems: 'center', justifyContent: 'center', flexDirection: 'row', width: '100%', paddingHorizontal: 24 }}>
                    {renderToolbarItem('plus-circle', 'Add Photos', () => photoPicker.current?.click())}
                    {renderToolbarItem('trash', 'Remove', () => setDeleteMode(true), photoData.length === 0)}
                    {renderToolbarItem('move', 'Re-arrange', () => setArrangeMode(true), photoData.length < 2)}
                </View>
            );
        }

        return(
            <Animatable.View
                animation={'slideInUp'}
                delay={1000}
                style={{
                    position: 'absolute', bottom: 24, backgroundColor: 'black', borderRadius: 8,
                    alignSelf: 'center', boxShadow: '0px 1px 5px #aeafb1, 0px -1px 5px #aeafb1'
                }}>
                {content}
            </Animatable.View>
        );
    };
    // endregion

    // region Render Photo Picker
    const renderPhotoPicker = () => (
        <input
            id={'photoPicker'}
            type={'file'}
            accept={'.png, .jpg, .jpeg'}
            multiple={true}
            ref={photoPicker}
            style={{ display: 'none' }}
            onChange={(event) => {
                event.stopPropagation();
                event.preventDefault();
                const fileList = Object.values(event.target.files || {});
                if (fileList.length > MAX_PHOTO_COUNT) {
                    showAlert({ text: `Please select ${MAX_PHOTO_COUNT} photos or fewer to upload at one time` });
                    return;
                }
                goUploadImageList(fileList);
            }}
        />
    )
    // endregion

    // region MAIN RENDER
    return(
        <View style={{ flex: 1, width: '100%' }}>
            {/* Nav Bar */}
            <NavBar
                leftButtonOnPress={() => RootNavigation.goBack()}
                title={'Truck Photo Album'}
                leftButtonIconName={'arrow-back'}
            />

            {/* Main Content */}
            <View onLayout={(e) => setContainerLayout(e.nativeEvent.layout)} style={{ flex: 1, width: '100%', backgroundColor: 'white' }}>
                {
                    isLoading ?
                        <LoadingView isLoading={true} />
                        :
                        <View style={{ flex: 1, width: '100%' }}>
                            {
                                onArrangeMode ?
                                    <DragDropProvider onDragEnd={_onDragEnd}>
                                        <ScrollView style={{ flex: 1, width: '100%', backgroundColor: 'white' }}>
                                            <View style={{ width: '100%', flexDirection: 'row', flexWrap: 'wrap' }}>
                                                {photoData.map((item, index) => (
                                                    <SortablePhotoItem
                                                        key={item.key}
                                                        item={item}
                                                        index={index}
                                                        position={index + 1}
                                                        cellWidthPct={`${100 / numColumns}%`}
                                                        cellHeight={300}
                                                    />
                                                ))}
                                            </View>
                                        </ScrollView>
                                    </DragDropProvider>
                                    :
                                    <FlatList
                                        ref={imageGrid}
                                        key={`grid_${numColumns}`}
                                        data={photoData}
                                        renderItem={renderPhotoItem}
                                        keyExtractor={keyExtractor}
                                        numColumns={numColumns}
                                        removeClippedSubviews={true}
                                        maxToRenderPerBatch={20}
                                        initialNumToRender={20}
                                        windowSize={5}
                                        extraData={deletingIDs}
                                        style={{ flex: 1, width: '100%', backgroundColor: 'white' }}
                                    />
                            }

                            {/* Toolbar */}
                            {renderToolbar()}

                            {/* Photo Picker */}
                            {renderPhotoPicker()}

                            {/* Lightbox - Truck Photos */}
                            <Lightbox
                                plugins={[Captions]}
                                open={photoCarouselOpened}
                                close={() => setPhotoCarouselOpened(false)}
                                index={currentPhotoIndex}
                                on={{ view: ({ index }) => setCurrentPhotoIndex(index) }}
                                slides={photoData}
                                carousel={{ imageFit: 'contain' }}
                                styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
                            />
                        </View>
                }
            </View>
        </View>
    );
    // endregion
}
